/**
 * PDF Download Handler
 * Handles PDF downloads with proper error handling and user feedback
 */
(function() {
  'use strict';

  function initPdfDownload() {
    const downloadButtons = document.querySelectorAll('.js-pdf-download');
    
    if (downloadButtons.length === 0) {
      return;
    }

    downloadButtons.forEach(function(button) {
      button.removeEventListener('click', handleDownloadClick);
      button.addEventListener('click', handleDownloadClick);
    });
  }

  /**
   * Handle PDF download button click
   * @param {Event} event - Click event
   */
  function handleDownloadClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const pdfUrl = button.getAttribute('data-pdf-url');
    const textElement = button.querySelector('.pdf-download__text');
    const loadingElement = button.querySelector('.pdf-download__loading');
    
    if (!pdfUrl) {
      console.error('PDF Download: PDF URL not found');
      return;
    }
    
    setLoadingState(button, textElement, loadingElement, true);

    downloadFile(pdfUrl)
      .then(function() {
        setTimeout(function() {
          setLoadingState(button, textElement, loadingElement, false);
        }, 1000);
      })
      .catch(function(error) {
        console.error('PDF Download failed:', error);
        
        setLoadingState(button, textElement, loadingElement, false);
        
        openInNewTab(pdfUrl);
      });
  }

  /**
   * Set loading state for download button
   * @param {HTMLElement} button - Download button
   * @param {HTMLElement} textElement - Text element to hide/show
   * @param {HTMLElement} loadingElement - Loading element to show/hide
   * @param {boolean} isLoading - Whether to show loading state
   */
  function setLoadingState(button, textElement, loadingElement, isLoading) {
    if (isLoading) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (textElement) textElement.classList.add('visually-hidden');
      if (loadingElement) loadingElement.classList.remove('visually-hidden');
    } else {
      button.removeAttribute('aria-busy');
      button.disabled = false;
      if (textElement) textElement.classList.remove('visually-hidden');
      if (loadingElement) loadingElement.classList.add('visually-hidden');
    }
  }

  /**
   * Extract filename from URL
   * @param {string} url - File URL
   * @returns {string} - Extracted filename
   */
  function extractFilenameFromUrl(url) {
    try {
      
      if (!url || typeof url !== 'string') {
        return 'document.pdf';
      }
      
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        const pathParts = url.split('/');
        let filename = pathParts[pathParts.length - 1];
        
        if (filename && filename.trim() !== '') {
          filename = filename.split('?')[0];
          return filename;
        }
        return 'document.pdf';
      }
      
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      
      const pathParts = pathname.split('/');
      
      let filename = pathParts[pathParts.length - 1];
      
      if (!filename || filename === '') {
        const filesIndex = pathParts.indexOf('files');
        
        if (filesIndex !== -1 && filesIndex + 1 < pathParts.length) {
          filename = pathParts[filesIndex + 1];
        }
      }
      
      if (filename) {
        filename = filename.split('?')[0];
      }
    
      if (filename && filename.trim() !== '') {
        return filename;
      }
      
      return 'document.pdf';
    } catch (error) {
      console.error('Could not extract filename from URL:', error);
      return 'document.pdf';
    }
  }

  /**
   * Download file using programmatic approach
   * @param {string} url - File URL
   * @param {string} customFilename - Custom filename (optional)
   * @returns {Promise} - Download promise
   */
  function downloadFile(url, customFilename) {
    return new Promise(function(resolve, reject) {
      try {
        const filename = customFilename || extractFilenameFromUrl(url);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        
        link.click();
        
        setTimeout(function() {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          resolve();
        }, 100);
        
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Open URL in new tab as fallback
   * @param {string} url - URL to open
   */
  function openInNewTab(url) {
    try {
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to open PDF in new tab:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdfDownload);
  } else {
    initPdfDownload();
  }

  if (typeof window.MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      let shouldReinit = false;
      
      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1 && (node.classList.contains('js-pdf-download') || node.querySelector('.js-pdf-download'))) {
              shouldReinit = true;
            }
          });
        }
      });
      
      if (shouldReinit) {
        initPdfDownload();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

})();